import type {
  Discount,
  DiscountAppliesTo,
  PatchAdminV1StoresStoreIdDiscountsIdBody,
  PostAdminV1StoresStoreIdDiscountsBody,
} from '@nucleus-commerce/api-client/admin';

import type { DiscountFormInput, DiscountFormOutput } from '../schemas/discount-form-schema';
import { fromDateTimeInput, toDateTimeInput, uniqueCodes } from '../schemas/discount-rules';

const toInt = (value: string) => (value === '' ? null : Number(value));
const toText = (value: number | null) => (value === null ? '' : String(value));

export function emptyDiscountForm(): DiscountFormInput {
  return {
    title: '',
    kind: 'code',
    method: 'percentage',
    percentage: '',
    amount: null,
    scope: 'order',
    productIds: [],
    collectionIds: [],
    minSubtotal: null,
    minQuantity: '',
    firstOrderOnly: false,
    countries: [],
    combinesWith: [],
    usageLimitTotal: '',
    usageLimitPerCustomer: '',
    startsAt: '',
    endsAt: '',
    status: 'active',
    codes: [],
  };
}

/** The backend stores a fraction and reads it back times 100; two decimals is what it accepts. */
function percentText(value: number | null): string {
  return value === null ? '' : String(Math.round(value * 100) / 100);
}

export function fromDiscount(discount: Discount): DiscountFormInput {
  const { appliesTo, conditions } = discount;
  return {
    title: discount.title,
    kind: discount.kind,
    method: discount.method,
    percentage: percentText(discount.percentage),
    amount: discount.amount,
    scope: appliesTo.scope,
    productIds: appliesTo.scope === 'products' ? appliesTo.productIds : [],
    collectionIds: appliesTo.scope === 'collections' ? appliesTo.collectionIds : [],
    minSubtotal: conditions.minSubtotal,
    minQuantity: toText(conditions.minQuantity),
    firstOrderOnly: conditions.firstOrderOnly,
    countries: conditions.countries ?? [],
    combinesWith: discount.combinesWith,
    usageLimitTotal: toText(discount.usageLimitTotal),
    usageLimitPerCustomer: toText(discount.usageLimitPerCustomer),
    startsAt: toDateTimeInput(discount.startsAt),
    endsAt: toDateTimeInput(discount.endsAt),
    status: discount.status,
    codes: discount.codes.map((c) => c.code),
  };
}

function appliesTo(values: DiscountFormOutput): DiscountAppliesTo {
  if (values.scope === 'products') return { scope: 'products', productIds: values.productIds };
  if (values.scope === 'collections') {
    return { scope: 'collections', collectionIds: values.collectionIds };
  }
  return { scope: 'order' };
}

/** Values of the methods not chosen are cleared, not kept. */
function common(values: DiscountFormOutput) {
  return {
    title: values.title,
    method: values.method,
    percentage: values.method === 'percentage' ? Number(values.percentage) : null,
    amount: values.method === 'fixed_amount' ? values.amount : null,
    appliesTo: appliesTo(values),
    conditions: {
      minSubtotal: values.minSubtotal,
      minQuantity: toInt(values.minQuantity),
      firstOrderOnly: values.firstOrderOnly,
      // The backend wants at least one country, or null for anywhere.
      countries: values.countries.length > 0 ? values.countries : null,
    },
    combinesWith: values.combinesWith,
    usageLimitTotal: toInt(values.usageLimitTotal),
    usageLimitPerCustomer: toInt(values.usageLimitPerCustomer),
    startsAt: fromDateTimeInput(values.startsAt),
    endsAt: fromDateTimeInput(values.endsAt),
    status: values.status,
  };
}

export function toDiscountCreate(
  values: DiscountFormOutput,
): PostAdminV1StoresStoreIdDiscountsBody {
  return {
    ...common(values),
    kind: values.kind,
    codes: values.kind === 'code' ? uniqueCodes(values.codes) : [],
  };
}

export function toDiscountUpdate(
  values: DiscountFormOutput,
): PatchAdminV1StoresStoreIdDiscountsIdBody {
  return common(values);
}
